import type { FieldPresentationBlock } from '#layers/data-model/constants/fields';
import type { AnyResolvedField } from '#layers/data-model/types/schema/resolved-schema';
import { getBlockRecommendedDisplayIds } from '#layers/block-engine/public';
import { getPrimitiveFieldPresentationDefaults, getRelationFieldDisplayDefaults } from '#layers/data-model/constants/fields';
import { resolveFieldPresentationBlock } from '#layers/data-model/utils/fields/presentation';

export interface FieldDisplayBlockOptions {
	block: string;
	props: Record<string, unknown>;
}

/**
 * Resolve the display block ID and user-configured props for a field.
 *
 * Field-level `displayId` is treated as the selected display block. Default
 * display props only apply when that selected block is the default block for the field.
 * Field-level `displayOptions` are passed through as props, along with `fieldId`.
 */
export function getFieldDisplayBlockOptions(field: AnyResolvedField): FieldDisplayBlockOptions {
	const display = resolveFieldPresentationBlock(getDefaultDisplayBlock(field), field.meta?.displayId, {
		fieldId: field.id,
		...field.meta?.displayOptions,
	});

	return {
		block: display.block,
		props: display.props ?? {},
	};
}

/**
 * Default display block used when a field has no explicit `displayId`.
 *
 * For primitive fields this follows the selected input's recommended display, so render matches the
 * appearance editor preview (which defaults the display to the chosen input's recommendation). The
 * primitive-type default — and its props — is kept when it already agrees or no input is selected.
 */
function getDefaultDisplayBlock(field: AnyResolvedField): FieldPresentationBlock {
	if ('oppositeCollectionId' in field) {
		return getRelationFieldDisplayDefaults(field);
	}

	const primitiveDefault = getPrimitiveFieldPresentationDefaults(field.type).display;
	const recommendedDisplayId = getBlockRecommendedDisplayIds(field.meta?.inputId, {
		typeOptions: field.meta?.typeOptions ?? undefined,
	})[0];

	return recommendedDisplayId && recommendedDisplayId !== primitiveDefault.block
		? { block: recommendedDisplayId }
		: primitiveDefault;
}
